// Services.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Services.css";
import { storage } from "../firebase";
import { ref, getDownloadURL } from "firebase/storage";

const Services = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("wedding");
  const [loading, setLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Firebase loaded images
  const [firebaseImages, setFirebaseImages] = useState({
    heroBg: "",
    ctaBg: "",
    portrait: "",
    wedding: "",
    commercial: "",
    event: "",
  });

  // Load all Firebase images
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const paths = {
          heroBg: "Services/backgroundNav.jpg",
          ctaBg: "Services/backgroundfoter.jpg",
          portrait: "Services/portrait.jpg",
          wedding: "Services/wedding.jpg",
          commercial: "Services/commercial.jpg",
          event: "Services/event.jpg",
        };

        const downloaded = {};

        for (const key in paths) {
          try {
            const imgRef = ref(storage, paths[key]);
            downloaded[key] = await getDownloadURL(imgRef);
          } catch (error) {
            console.error(`Error loading ${key}:`, error);
            // Set fallback color or gradient
            downloaded[key] = `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`;
          }
        }

        setFirebaseImages(downloaded);
        setImagesLoaded(true);
        setTimeout(() => setLoading(false), 500); // Show loading for minimum time
      } catch (error) {
        console.error("Error loading images:", error);
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  const servicesData = {
    wedding: {
      title: "Wedding Photography",
      description: "Comprehensive wedding coverage to capture your special day perfectly.",
      price: "",
      features: [
        "Full-day or half-day wedding coverage",
        "Pre-wedding / engagement photoshoot",
        "Bride & groom getting-ready coverage",
        "Ceremony and reception photography",
        "Candid & cinematic style portraits",
        "Family and group photos",
        "Professional lighting and creative poses",
        "50–200 fully edited high-resolution photos",
        "Highlight album with the best moments",
        "Optional wedding photo album",
      ],
      image: firebaseImages.wedding,
      icon: "💒"
    },
    portrait: {
      title: "Portrait Photography",
      description: "Capture beautiful moments and emotions with professional portrait sessions.",
      price: "",
      features: [
        "1–2 hour personalized photoshoot",
        "Studio or outdoor session options",
        "Professional lighting and posing guidance",
        "Unlimited outfit changes",
        "20–30 fully edited high-resolution photos",
        "Creative and cinematic portrait styles",
        "Solo, couple, or family portrait options",
        "Print-ready images included",
        "Online gallery for viewing & downloading",
        "Fast delivery",
      ],
      image: firebaseImages.portrait,
      icon: "👤"
    },
    commercial: {
      title: "Commercial Photography",
      description: "Professional product and commercial photography for businesses.",
      price: "",
      features: [
        "Corporate portraits & headshots",
        "Lifestyle product photography",
        "Fashion and apparel shoots",
        "Real estate & architectural photography",
        "E-commerce photo packages",
        "Commercial usage rights included",
        "Social media optimized versions",
        "Background removal & retouching",
        "High-resolution edited images",
        "Creative lighting and styling setups",
      ],
      image: firebaseImages.commercial,
      icon: "🏢"
    },
    event: {
      title: "Event Photography",
      description: "Capture the energy and moments of your corporate or social events.",
      price: "",
      features: [
        "Coverage for any type of event",
        "2–6 hour photography coverage",
        "Candid and posed photos of guests",
        "Stage, performance, and activity coverage",
        "Group and VIP guest portraits",
        "Professional lighting setup when needed",
        "High-quality edited photos",
        "Optional same-day preview highlights",
        "Print-ready high-resolution images",
        "Online gallery for viewing & downloading",
      ],
      image: firebaseImages.event,
      icon: "🎉"
    },
  };

  const packages = [
    {
      name: "Basic",
      price: "",
      features: [
        "Short session",
        "10–15 edited photos",
        "1 location",
        "Standard lighting",
        "Online gallery for download",
        "Print-ready images",
      ],
      popular: false,
    },
    {
      name: "Professional",
      price: "",
      features: [
        "Full session",
        "20–30 edited photos",
        "Multiple poses & styles",
        "1–2 locations",
        "Professional lighting setup",
        "Outfit changes allowed",
        "Online gallery + high-resolution images",
      ],
      popular: true,
    },
    {
      name: "Premium",
      price: "",
      features: [
        "Extended session",
        "40–60 fully edited photos",
        "Multiple locations",
        "Unlimited outfit changes",
        "Creative lighting & styling",
        "Priority editing",
        "Online gallery + USB or album",
        "Print-quality high-resolution images",
      ],
      popular: false,
    },
  ];

  // Handle package selection
  const handleSelectPackage = (packageName) => {
    navigate("/contact", { 
      state: { selectedPackage: packageName }
    });
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section
        className="services-hero"
        style={{
          backgroundImage: `url(${firebaseImages.heroBg})`,
        }}
      >
        <div className="hero-content">
          <h1>Photography Services</h1>
          <p>
            Capturing moments that last a lifetime with professional photography
            services
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <h2>Our Services</h2>

          <div className="services-tabs">
            {Object.keys(servicesData).map((service) => (
              <button
                key={service}
                className={`tab-button ${
                  activeTab === service ? "active" : ""
                }`}
                onClick={() => setActiveTab(service)}
              >
                {servicesData[service].icon} {servicesData[service].title}
              </button>
            ))}
          </div>

          <div className="service-details">
            <div className="service-info">
              <h3>{servicesData[activeTab].title}</h3>
              <p>{servicesData[activeTab].description}</p>
              
              <div className="price-display">
                <strong>{servicesData[activeTab].price}</strong>
              </div>

              <ul className="features-list">
                {servicesData[activeTab].features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>

            <div className="service-image">
              <img
                src={servicesData[activeTab].image}
                alt={servicesData[activeTab].title}
                onError={(e) => {
                  e.target.style.background = `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`;
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="container">
          <h2>Packages</h2>

          <div className="pricing-grid">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`pricing-card ${pkg.popular ? "popular" : ""}`}
              >
                {pkg.popular && (
                  <div className="popular-badge">Most Popular</div>
                )}

                <h3>{pkg.name}</h3>
                <div className="price">{pkg.price}</div>

                <ul className="package-features">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>

                <button
                  className="package-button"
                  onClick={() => handleSelectPackage(pkg.name)}
                >
                  Select Package
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <h2>How It Works</h2>

          <div className="process-steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Consultation</h3>
              <p>We discuss your vision, preferences, and requirements</p>
            </div>

            <div className="step">
              <div className="step-number">2</div>
              <h3>Photoshoot</h3>
              <p>Professional photoshoot at your chosen location</p>
            </div>

            <div className="step">
              <div className="step-number">3</div>
              <h3>Editing</h3>
              <p>Careful selection and professional editing of your photos</p>
            </div>

            <div className="step">
              <div className="step-number">4</div>
              <h3>Delivery</h3>
              <p>Receive your beautiful photos in digital and print formats</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="cta-section"
        style={{
          backgroundImage: `url(${firebaseImages.ctaBg})`,
        }}
      >
        <div className="container">
          <h2>Ready to Create Beautiful Memories?</h2>
          <p>Contact us today to schedule your photoshoot</p>
          <button 
            className="package-button2"
            style={{
              marginTop: '2rem',
              padding: '1rem 3rem',
              display: 'inline-block',
              width: 'auto'
            }}
            onClick={() => navigate('/contact')}
          >
            Book Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default Services;
